/**
 * Grille des paiements période par période avec statut visuel clair.
 */

import React from 'react';
import { Check, X, AlertCircle, Circle, Clock } from 'lucide-react';
import { Paiement } from '../services/verifApi';

const STATUT_META: Record<Paiement['statut'], { label: string; color: string; icon: any }> = {
  a_jour:      { label: 'À jour',       color: 'bg-emerald-100 border-emerald-400 text-emerald-800', icon: Check },
  avance:      { label: 'Avance',       color: 'bg-blue-100 border-blue-400 text-blue-800',       icon: Check },
  reliquat:    { label: 'Reliquat',     color: 'bg-amber-100 border-amber-400 text-amber-800',    icon: AlertCircle },
  impaye:      { label: 'Impayé',       color: 'bg-red-100 border-red-400 text-red-800',          icon: X },
  non_importe: { label: 'Non déclaré',  color: 'bg-gray-100 border-gray-300 text-gray-600',       icon: Circle },
};

const fmt = (v: string) => {
  const n = Number(v || 0);
  if (Number.isNaN(n) || n === 0) return '0';
  return n.toLocaleString('fr-FR');
};

interface Props {
  paiements: Paiement[];
}

export default function StatutPaiements({ paiements }: Props) {
  if (!paiements || paiements.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
        Aucune période de paiement enregistrée.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {paiements.map(p => {
        const meta = STATUT_META[p.statut] || STATUT_META.non_importe;
        const Icon = meta.icon;
        return (
          <div
            key={p.periode}
            className={`p-3 rounded-lg border-2 ${meta.color}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-bold uppercase tracking-wide">
                {p.periode}
              </div>
              <Icon size={14} />
            </div>
            <div className="text-[11px] leading-tight opacity-80 mb-1">
              {p.libelle}
            </div>
            <div className="text-[10px] font-semibold uppercase mb-1">
              {meta.label}
              {p.saisie_manuelle && (
                <span className="ml-1 text-[9px] opacity-60">(manuel)</span>
              )}
            </div>
            {p.statut !== 'non_importe' && (
              <div className="text-[11px] space-y-0.5 pt-1 border-t border-current/20">
                <div className="flex justify-between">
                  <span className="opacity-70">Payé</span>
                  <span className="font-mono">{fmt(p.frais_payes)}</span>
                </div>
                {Number(p.solde_pratique) > 0 && (
                  <div className="flex justify-between font-semibold">
                    <span>Reste</span>
                    <span className="font-mono">{fmt(p.solde_pratique)}</span>
                  </div>
                )}
              </div>
            )}
            {p.statut === 'non_importe' && p.requise && (
              <div className="text-[10px] mt-1 flex items-center gap-1 text-amber-700">
                <Clock size={10} /> Attendu
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
